import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Archive,
  ExternalLink,
  ImagePlus,
  Pencil,
  Play,
  Plus,
  Save,
  Square,
  Trash2,
  X,
} from 'lucide-react';
import { toast } from 'react-toastify';
import { useUser } from '../../../contexts/UserContext';
import PermissionDeniedPage from '../../../pages/PermissionDeniedPage';
import {
  createAnnouncement,
  deleteAnnouncement,
  listAnnouncements,
  setAnnouncementStatus,
  updateAnnouncement,
  uploadAnnouncementImage,
} from '../../../services/api/announcement.service';
import type {
  Announcement,
  AnnouncementInput,
  AnnouncementStatus,
} from '../../../types/announcement';
import {
  isAnnouncementJwtTargetAllowed,
  openAnnouncementTarget,
} from '../../../utils/announcementTarget';

const statusStyles: Record<AnnouncementStatus, string> = {
  draft: 'bg-gray-100 text-gray-700',
  active: 'bg-green-100 text-green-700',
  inactive: 'bg-amber-100 text-amber-700',
  archived: 'bg-red-100 text-red-700',
};

const StatusBadge: React.FC<{ status: AnnouncementStatus }> = ({ status }) => (
  <span className={`rounded-full px-2 py-0.5 text-[0.65rem] font-medium ${statusStyles[status]}`}>
    {status}
  </span>
);

const Announcements: React.FC = () => {
  const { userProfile } = useUser();
  const systemRole = (() => {
    try { return sessionStorage.getItem('systemRole') || userProfile?.systemRole || null; }
    catch { return null; }
  })();
  const isSystemAdmin = systemRole === 'system_admin';

  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [editing, setEditing] = useState<Announcement | null | 'new'>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setAnnouncements(await listAnnouncements());
    } catch (error: any) {
      toast.error(error?.message || 'Failed to load announcements');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isSystemAdmin && editing === null) void load();
  }, [isSystemAdmin, editing, load]);

  if (!isSystemAdmin) return <PermissionDeniedPage />;

  const changeStatus = async (announcement: Announcement, status: AnnouncementStatus) => {
    try {
      setBusyId(announcement.id);
      await setAnnouncementStatus(announcement.id, status);
      toast.success(status === 'active' ? 'Announcement published' : 'Announcement deactivated');
      await load();
    } catch (error: any) {
      toast.error(error?.message || 'Failed to update announcement');
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (announcement: Announcement) => {
    if (!window.confirm('Delete this announcement? This cannot be undone.')) return;
    try {
      setBusyId(announcement.id);
      await deleteAnnouncement(announcement.id);
      toast.success('Announcement deleted');
      await load();
    } catch (error: any) {
      toast.error(error?.message || 'Failed to delete announcement');
    } finally {
      setBusyId(null);
    }
  };

  if (editing !== null) {
    return (
      <AnnouncementEditor
        initial={editing === 'new' ? null : editing}
        onCancel={() => setEditing(null)}
        onSaved={() => setEditing(null)}
      />
    );
  }

  return (
    <div className="mx-auto max-w-6xl p-4 md:p-6">
      <div className="mb-6 flex items-center justify-between gap-4">
        <div>
          <h1 className="text-lg font-semibold text-gray-900">Announcements</h1>
          <p className="text-[0.75rem] text-gray-500">
            Show one image announcement to users after login.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setEditing('new')}
          className="flex items-center gap-1.5 rounded-lg bg-black px-3 py-2 text-[0.75rem] font-medium text-white hover:bg-gray-800"
        >
          <Plus size={14} /> New announcement
        </button>
      </div>

      <div className="mb-4 rounded-lg border border-blue-100 bg-blue-50 px-4 py-3 text-[0.72rem] text-blue-800">
        Publishing an announcement automatically deactivates the currently active one.
      </div>

      {loading ? (
        <div className="py-12 text-center text-[0.8rem] text-gray-500">Loading…</div>
      ) : announcements.length === 0 ? (
        <div className="rounded-xl border border-dashed border-gray-200 py-16 text-center text-[0.8rem] text-gray-500">
          No announcements yet.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-gray-100">
          <table className="w-full text-[0.75rem]">
            <thead className="bg-gray-50 text-gray-500">
              <tr>
                <th className="px-3 py-2 text-left font-medium">Creative</th>
                <th className="px-3 py-2 text-left font-medium">Button</th>
                <th className="px-3 py-2 text-left font-medium">Status</th>
                <th className="px-3 py-2 text-right font-medium">Dismissals</th>
                <th className="px-3 py-2 text-right font-medium">Clicks</th>
                <th className="px-3 py-2 text-right font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {announcements.map((announcement) => {
                const status = announcement.status || 'draft';
                return (
                  <tr key={announcement.id} className="border-t border-gray-100 align-middle hover:bg-gray-50">
                    <td className="px-3 py-2.5">
                      <img
                        src={announcement.image_url}
                        alt="Announcement creative"
                        className="h-14 w-24 rounded-md border border-gray-100 bg-gray-50 object-contain"
                      />
                    </td>
                    <td className="max-w-xs px-3 py-2.5">
                      <div className="font-medium text-gray-900">{announcement.button_label}</div>
                      {announcement.attach_jwt && (
                        <span className="mr-1 inline-flex rounded bg-blue-50 px-1.5 py-0.5 text-[0.6rem] font-medium text-blue-700">
                          JWT attached
                        </span>
                      )}
                      <a
                        href={announcement.target_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 truncate text-[0.68rem] text-gray-400 hover:text-black"
                      >
                        {announcement.target_url} <ExternalLink size={10} />
                      </a>
                    </td>
                    <td className="px-3 py-2.5"><StatusBadge status={status} /></td>
                    <td className="px-3 py-2.5 text-right tabular-nums text-gray-600">
                      {announcement.interaction_count ?? 0}
                    </td>
                    <td className="px-3 py-2.5 text-right tabular-nums text-gray-600">
                      {announcement.click_count ?? 0}
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          title="Edit"
                          type="button"
                          disabled={busyId === announcement.id}
                          onClick={() => setEditing(announcement)}
                          className="rounded p-1.5 text-gray-600 hover:bg-gray-200 disabled:opacity-40"
                        >
                          <Pencil size={14} />
                        </button>
                        {status === 'active' ? (
                          <button
                            title="Deactivate"
                            type="button"
                            disabled={busyId === announcement.id}
                            onClick={() => void changeStatus(announcement, 'inactive')}
                            className="rounded p-1.5 text-amber-700 hover:bg-amber-100 disabled:opacity-40"
                          >
                            <Square size={14} />
                          </button>
                        ) : (
                          <button
                            title="Publish"
                            type="button"
                            disabled={busyId === announcement.id}
                            onClick={() => void changeStatus(announcement, 'active')}
                            className="rounded p-1.5 text-green-700 hover:bg-green-100 disabled:opacity-40"
                          >
                            <Play size={14} />
                          </button>
                        )}
                        <button
                          title="Delete"
                          type="button"
                          disabled={busyId === announcement.id}
                          onClick={() => void remove(announcement)}
                          className="rounded p-1.5 text-red-600 hover:bg-red-100 disabled:opacity-40"
                        >
                          {status === 'active' ? <Archive size={14} /> : <Trash2 size={14} />}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const AnnouncementEditor: React.FC<{
  initial: Announcement | null;
  onCancel: () => void;
  onSaved: () => void;
}> = ({ initial, onCancel, onSaved }) => {
  const [buttonLabel, setButtonLabel] = useState(initial?.button_label || 'Learn more');
  const [targetUrl, setTargetUrl] = useState(initial?.target_url || '');
  const [attachJwt, setAttachJwt] = useState(initial?.attach_jwt || false);
  const [status, setStatus] = useState<AnnouncementStatus>(initial?.status || 'draft');
  const [file, setFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  const [openingPreview, setOpeningPreview] = useState(false);

  const previewUrl = useMemo(() => file ? URL.createObjectURL(file) : initial?.image_url || '', [file, initial]);
  useEffect(() => () => {
    if (file && previewUrl.startsWith('blob:')) URL.revokeObjectURL(previewUrl);
  }, [file, previewUrl]);

  const save = async () => {
    if (!buttonLabel.trim()) return toast.error('Button label is required');
    try {
      const parsed = new URL(targetUrl);
      if (!['http:', 'https:'].includes(parsed.protocol)) throw new Error();
    } catch {
      return toast.error('Enter a valid HTTP or HTTPS destination URL');
    }
    if (attachJwt && !isAnnouncementJwtTargetAllowed(targetUrl)) {
      return toast.error('JWT links must use novadistro.io or one of its subdomains');
    }
    if (!initial && !file) return toast.error('Choose an announcement image');

    try {
      setSaving(true);
      let imageUrl = initial?.image_url || '';
      let storagePath = initial?.image_storage_path || '';
      let storageBucket = initial?.image_storage_bucket || '';
      if (file) {
        const uploaded = await uploadAnnouncementImage(file);
        imageUrl = uploaded.imageUrl;
        storagePath = uploaded.storagePath;
        storageBucket = uploaded.bucket;
      }

      const input: AnnouncementInput = {
        image_url: imageUrl,
        image_storage_path: storagePath,
        image_storage_bucket: storageBucket,
        target_url: targetUrl.trim(),
        button_label: buttonLabel.trim(),
        attach_jwt: attachJwt,
        status,
      };
      if (initial) await updateAnnouncement(initial.id, input);
      else await createAnnouncement(input);

      toast.success(initial ? 'Announcement updated' : 'Announcement created');
      onSaved();
    } catch (error: any) {
      toast.error(error?.message || 'Failed to save announcement');
    } finally {
      setSaving(false);
    }
  };

  const openPreview = async () => {
    if (!targetUrl.trim()) return toast.error('Enter a destination URL to test the button');
    try {
      setOpeningPreview(true);
      await openAnnouncementTarget(targetUrl.trim(), attachJwt);
    } catch (error: any) {
      toast.error(error?.message || 'Failed to open preview link');
    } finally {
      setOpeningPreview(false);
    }
  };

  return (
    <div className="mx-auto max-w-5xl p-4 md:p-6">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-lg font-semibold text-gray-900">
            {initial ? 'Edit announcement' : 'New announcement'}
          </h1>
          <p className="text-[0.75rem] text-gray-500">Upload the creative and configure its call to action.</p>
        </div>
        <button type="button" onClick={onCancel} aria-label="Close editor" className="rounded-full p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-700">
          <X size={18} />
        </button>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(340px,0.8fr)]">
        <div className="space-y-5 rounded-xl border border-gray-100 bg-white p-5">
          <div>
            <label className="mb-1 block text-[0.72rem] font-medium text-gray-700">Image</label>
            <label className="flex cursor-pointer items-center justify-center gap-2 rounded-lg border border-dashed border-gray-300 px-4 py-7 text-[0.75rem] text-gray-600 hover:border-gray-500 hover:bg-gray-50">
              <ImagePlus size={17} />
              {file ? file.name : initial ? 'Replace image' : 'Choose image'}
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="sr-only"
                onChange={(event) => setFile(event.target.files?.[0] || null)}
              />
            </label>
            <p className="mt-1 text-[0.65rem] text-gray-400">JPG, PNG or WebP, up to 5 MB. Landscape artwork around 1.91:1 works best.</p>
          </div>

          <div>
            <label className="mb-1 block text-[0.72rem] font-medium text-gray-700">Destination link</label>
            <input
              type="url"
              value={targetUrl}
              onChange={(event) => setTargetUrl(event.target.value)}
              placeholder="https://example.com/"
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-[0.8rem] focus:outline-none focus:ring-1 focus:ring-black"
            />
            <label className="mt-3 flex cursor-pointer items-start gap-2.5 rounded-lg border border-gray-100 bg-gray-50 px-3 py-2.5">
              <input
                type="checkbox"
                checked={attachJwt}
                onChange={(event) => setAttachJwt(event.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-gray-300 text-black focus:ring-black"
              />
              <span>
                <span className="block text-[0.72rem] font-medium text-gray-700">Attach Nova JWT token</span>
                <span className="mt-0.5 block text-[0.65rem] leading-4 text-gray-400">
                  Adds the signed-in user token to the link as #t=… Only novadistro.io links are allowed.
                </span>
              </span>
            </label>
          </div>

          <div>
            <label className="mb-1 block text-[0.72rem] font-medium text-gray-700">Button label</label>
            <input
              value={buttonLabel}
              maxLength={80}
              onChange={(event) => setButtonLabel(event.target.value)}
              placeholder="Explore Nova MyLinks"
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-[0.8rem] focus:outline-none focus:ring-1 focus:ring-black"
            />
          </div>

          <div>
            <label className="mb-1 block text-[0.72rem] font-medium text-gray-700">Status</label>
            <select
              value={status}
              onChange={(event) => setStatus(event.target.value as AnnouncementStatus)}
              className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-[0.8rem] focus:outline-none focus:ring-1 focus:ring-black"
            >
              <option value="draft">Draft</option>
              <option value="active">Active — publish now</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onCancel} className="rounded-lg border border-gray-200 px-4 py-2 text-[0.75rem] text-gray-600 hover:bg-gray-50">
              Cancel
            </button>
            <button
              type="button"
              disabled={saving}
              onClick={() => void save()}
              className="flex items-center gap-1.5 rounded-lg bg-black px-4 py-2 text-[0.75rem] font-medium text-white hover:bg-gray-800 disabled:opacity-50"
            >
              <Save size={14} /> {saving ? 'Saving…' : 'Save announcement'}
            </button>
          </div>
        </div>

        <div>
          <div className="mb-2 text-[0.68rem] font-semibold uppercase tracking-wide text-gray-400">Preview</div>
          <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-xl">
            <div className="flex min-h-52 items-center justify-center bg-[#f8f7f2]">
              {previewUrl ? (
                <img src={previewUrl} alt="Announcement preview" className="max-h-[420px] w-full object-contain" />
              ) : (
                <div className="flex flex-col items-center gap-2 py-20 text-[0.72rem] text-gray-400">
                  <ImagePlus size={24} /> Select an image to preview
                </div>
              )}
            </div>
            <div className="border-t border-gray-100 p-4">
              <button
                type="button"
                disabled={openingPreview}
                onClick={() => void openPreview()}
                className="flex w-full items-center justify-center rounded-full bg-black px-5 py-3 text-sm font-semibold text-white transition hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2 disabled:cursor-wait disabled:opacity-60"
              >
                {openingPreview ? 'Opening…' : buttonLabel.trim() || 'Button label'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Announcements;
